import React, { useEffect, useMemo, useState } from 'react';
import { APP_ROUTES, PUBLIC_PATH } from '../../../config/routes-config';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { Link } from 'react-router-dom';
import { NavLinkItem } from '../../../interfaces/nav-link.interface';
import { RootState } from '../../../redux/reducers';
import { connect } from 'react-redux';

import useWindowResizer from '../../../hooks/window-resizer/tablet-resizer.hook';
import { MenuIcon, WarningIcon } from '../../../../ui/Icons';
import Popover from '../../../../ui/Popover';
import Modal from '../../../../ui/Model';
import { getCachedDoubleBookings } from '../../../services/double-bookings-cache';
import DoubleBookingsPopover, {
  DoubleBookingItem,
  ChannelLogos,
  formatDate
} from './double-bookings-popover.component';
import { ReservationStatus } from '../../../enums/reservation-status';

export interface GlobalHeaderProps {
  userEmail?: string;
  backLink?: NavLinkItem | null;
  toggleMobileMenu: () => void;
}

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  backLink,
  children,
  toggleMobileMenu
}) => {
  const isTabletView = useWindowResizer();

  const [doubleBookings, setDoubleBookings] = useState<DoubleBookingItem[]>([]);
  const [warningAnchorEl, setWarningAnchorEl] = useState<HTMLElement | null>(null);
  const [isDoubleBookingsModalOpen, setIsDoubleBookingsModalOpen] = useState(false);

  useEffect(() => {
    getCachedDoubleBookings().then(setDoubleBookings);
  }, []);

  const doubleBookingsCount = useMemo(() => doubleBookings?.length || 0, [doubleBookings]);
  const hasDoubleBookings = doubleBookingsCount > 0;

  const isWarningOpen = Boolean(warningAnchorEl);
  const closeWarningPopover = () => setWarningAnchorEl(null);

  const openDoubleBookingsModal = () => {
    closeWarningPopover();
    setIsDoubleBookingsModalOpen(true);
  };

  const closeDoubleBookingsModal = () => setIsDoubleBookingsModalOpen(false);

  const getStatusBadgeClassName = (status?: string) => {
    switch (status) {
      case ReservationStatus.CHECKED_IN_STARTED:
      case ReservationStatus.IN_HOUSE:
      case ReservationStatus.CHECKED_IN:
        return 'bg-amber-100 text-amber-900 border-amber-200';
      case ReservationStatus.PENDING_CHECK_IN:
      case ReservationStatus.CONFIRMATION_PENDING:
        return 'bg-blue-100 text-blue-900 border-blue-200';
      case ReservationStatus.CHECKED_OUT:
        return 'bg-green-100 text-green-900 border-green-200';
      case ReservationStatus.CANCELED:
      case ReservationStatus.EXPIRED:
        return 'bg-red-100 text-red-900 border-red-200';
      default:
        return 'bg-neutral-100 text-neutral-700 border-neutral-200';
    }
  };

  const formatStatusLabel = (status?: string) => {
    if (!status) return '-';
    const normalized = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return normalized;
  };

  return (
    <header className="h-[45px] bg-primary-500 text-neutral-0 border-b border-gray-200 flex items-center pr-0">
      {isTabletView && backLink ? (
        <>
          <Link
            to={{
              pathname: backLink.navLink
            }}
          >
            <div className="flex items-center text-neutral-0 ml-6 text-[1.6rem]">
              <ArrowBackIosIcon color="inherit" fontSize="inherit" /> {backLink.title}
            </div>
          </Link>
          <button type="button" onClick={toggleMobileMenu} className="ml-auto">
            <MenuIcon fill="white" />
          </button>
        </>
      ) : (
        <>
          <Link
            to={{
              pathname: APP_ROUTES.HOME_MODULE.FULL_PATH
            }}
            className="flex items-center justify-center h-full"
          >
            <img
              src={`${PUBLIC_PATH}/assets/svgs/birdnest-logo-white.svg`}
              alt="birdnestlife logo"
              className="ml-4 h-full max-h-[20px]"
            />
          </Link>
          <button type="button" className="ml-6" onClick={toggleMobileMenu}>
            <MenuIcon fill="white" />
          </button>
        </>
      )}
      <div className="flex items-center h-full flex-1">
        {children ? <div className="w-3/4">{children}</div> : ''}
      </div>

      <div className="flex items-center h-full ml-auto mr-6">
        {hasDoubleBookings ? (
          <>
            <button
              type="button"
              aria-label={`Double bookings: ${doubleBookingsCount}`}
              className="relative inline-flex items-center justify-center"
              onClick={(e) => setWarningAnchorEl(e.currentTarget)}
            >
              <span className="relative inline-flex items-center justify-center animate-pulse">
                <span className="absolute top-0 left-0 inline-flex h-full w-full animate-ping rounded-full bg-red-500/60" />
                <WarningIcon color="red" />
              </span>
            </button>

            <Popover
              open={isWarningOpen}
              anchorEl={warningAnchorEl}
              onClose={closeWarningPopover}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <DoubleBookingsPopover
                doubleBookings={doubleBookings}
                onClosePopover={closeWarningPopover}
                onShowDetails={openDoubleBookingsModal}
              />
            </Popover>

            <Modal
              isOpen={isDoubleBookingsModalOpen}
              onClose={closeDoubleBookingsModal}
              headerContent={`Double bookings (${doubleBookingsCount})`}
              closeIconPosition="right"
              contentClassName="gap-4"
              footerContent={
                <button
                  type="button"
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                  onClick={closeDoubleBookingsModal}
                >
                  Close
                </button>
              }
            >
              <div className="w-full">
                {doubleBookingsCount === 0 ? (
                  <div className="text-sm text-neutral-500">No double bookings found.</div>
                ) : (
                  <div className="flex w-full flex-col gap-3">
                    {doubleBookings.map((booking, index) => (
                      <div
                        key={
                          booking.otherReservation?.id ||
                          booking.blockedDate?.id ||
                          booking.currentReservation.id ||
                          index
                        }
                        className="w-full rounded-lg border border-gray-200 bg-white p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-xl font-semibold text-neutral-800">
                                  {booking.currentReservation.unitName ||
                                    booking.currentReservation.roomId}
                                </div>
                              </div>
                            </div>
                            <div className="mt-1 text-xl text-neutral-600 flex flex-row gap-1 items-center">
                              #{booking.currentReservation.id} •{' '}
                              {booking.currentReservation.primaryGuestName}
                              <ChannelLogos
                                source={booking.currentReservation.source as any}
                                channel={booking.currentReservation.channel as any}
                              />
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xl text-neutral-600">
                              <span>
                                {formatDate(booking.currentReservation.checkInDate)} →{' '}
                                {formatDate(booking.currentReservation.checkOutDate)}
                              </span>
                              <span
                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xl font-semibold ${getStatusBadgeClassName(
                                  booking.currentReservation.status
                                )}`}
                              >
                                {formatStatusLabel(booking.currentReservation.status)}
                              </span>
                            </div>
                          </div>

                          <div className="shrink-0 flex items-center gap-3">
                            <button
                              type="button"
                              className="text-xl text-primary-600 underline hover:text-primary-800"
                              onClick={() =>
                                window.open(
                                  PUBLIC_PATH +
                                    (
                                      APP_ROUTES.RESERVATION_MODULE.CHILDREN as any
                                    ).EDIT.FULL_PATH.replace(':id', booking.currentReservation.id),
                                  '_blank'
                                )
                              }
                            >
                              Open
                            </button>

                            {booking.blockedDate?.id ? (
                              <button
                                type="button"
                                className="text-xl text-primary-600 underline hover:text-primary-800"
                                onClick={() =>
                                  window.open(
                                    PUBLIC_PATH +
                                      (APP_ROUTES.BLOCK_DATES_MODULE.CHILDREN as any).LIST
                                        .FULL_PATH,
                                    '_blank'
                                  )
                                }
                              >
                                Open block dates
                              </button>
                            ) : null}
                          </div>
                        </div>

                        {booking.otherReservation ? (
                          <div className="mt-3 rounded-md bg-gray-50 p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xl font-semibold text-neutral-700">
                                Conflicts with
                              </div>

                              {booking.otherReservation?.id ? (
                                <button
                                  type="button"
                                  className="text-xl text-primary-600 underline hover:text-primary-800"
                                  onClick={() =>
                                    window.open(
                                      PUBLIC_PATH +
                                        (
                                          APP_ROUTES.RESERVATION_MODULE.CHILDREN as any
                                        ).EDIT.FULL_PATH.replace(
                                          ':id',
                                          booking.otherReservation?.id
                                        ),
                                      '_blank'
                                    )
                                  }
                                >
                                  Open other
                                </button>
                              ) : null}
                            </div>

                            <div className="mt-1 text-xl text-neutral-600 flex flex-row gap-4 items-center">
                              <div>
                                #{booking.otherReservation.id} •{' '}
                                {booking.otherReservation.primaryGuestName}{' '}
                              </div>
                              <ChannelLogos
                                source={booking.otherReservation.source as any}
                                channel={booking.otherReservation.channel as any}
                              />
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xl text-neutral-600">
                              <span>
                                {formatDate(booking.otherReservation.checkInDate)} →{' '}
                                {formatDate(booking.otherReservation.checkOutDate)}
                              </span>
                              <span
                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xl font-semibold ${getStatusBadgeClassName(
                                  booking.otherReservation.status
                                )}`}
                              >
                                {formatStatusLabel(booking.otherReservation.status)}
                              </span>
                            </div>
                          </div>
                        ) : booking.blockedDate ? (
                          <div className="mt-3 rounded-md bg-gray-50 p-3">
                            <div className="text-xl font-semibold text-neutral-700">
                              Conflicts with blocked date
                            </div>
                            <div className="mt-1 text-xl text-neutral-600">
                              #{booking.blockedDate.id}
                              {booking.blockedDate.description
                                ? ` • ${booking.blockedDate.description}`
                                : ''}
                            </div>
                            <div className="mt-1 text-xl text-neutral-600">
                              {formatDate(booking.blockedDate.fromDate)} →{' '}
                              {formatDate(booking.blockedDate.toDate)}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Modal>
          </>
        ) : null}
      </div>
    </header>
  );
};

function mapStateToProps(state: RootState) {
  return {
    backLink: state.appConfig.headerBackLink
  };
}

export default connect(mapStateToProps)(GlobalHeader);
